import { VaultFileFromNewerAppError, VaultWriteRefusedError, goneMessage, refusalMessage } from './sparseWriter';
import type { FileChange, FileWrite, RefusedFile } from './sparseWriter';

/**
 * Edits wait here before they are written.
 *
 * Nothing wrote per-frame — a board reorder and a plane drag both commit on
 * the drop — but each gesture was still a write, and a write here is not what
 * a write is in most apps: re-encrypt the file, upload its blob, CAS the
 * manifest, wake every other device. Reordering five arcs was five manifest
 * versions and five fan-outs.
 *
 * So edits coalesce per path, newest wins, and go out together once the
 * gestures stop. It also cuts CAS races with the phone, which is the second
 * reason rather than a bonus.
 *
 * The debounce is only safe with three guarantees, and all three are here:
 *
 * 1. **Nothing is lost on the way out.** A hidden tab or a closing one
 *    flushes immediately; a debounce that can drop a change on close is a
 *    debounce nobody should trust.
 * 2. **Reads see pending edits.** Callers overlay [pending] on what they load,
 *    so a change is visible the moment it is made rather than when it lands.
 * 3. **The state is honest.** `unsaved` is a distinct state from `saving`, so
 *    the bar never claims a change reached the vault before it did.
 *
 * A path's changes coalesce too: what goes out applies each of them, in order,
 * to the file as it stands then — never to a write already on its way, which
 * has landed by the time the next one goes and would be applied twice.
 */

export type WriteState = 'idle' | 'unsaved' | 'saving' | 'error';

export interface QueueView {
  state: WriteState;
  /** path → the file exactly as it should be written. */
  pending: Map<string, string>;
  error: string | null;
  /** The batch behind `error` when the vault refused it as it stands and it
   *  was dropped rather than kept. */
  dropped: DroppedBatch | null;
  /** The files dropped from their batches because a newer app wrote them, in the order they were
   *  refused, until the next edit. A new array whenever one is added, so a watcher can tell a new
   *  refusal from one it has seen. */
  refused: readonly string[];
}

export interface DroppedBatch {
  fileCount: number;
  atMs: number;
  /** Why it could not be sent: more than one record holds, or a refusal it could not take a file out of. */
  cause: 'too-big' | 'newer-app';
}

/** Long enough that a run of edits becomes one write, short enough that a
 *  pause reads as done. Five seconds was chosen by using the thing. */
const QUIET_MS = 5000;

type Flusher = (writes: FileWrite[]) => Promise<unknown>;

/** What a path is waiting to have written: the text the page shows, and the changes that make it,
 *  or null when it is a whole file. Each change keeps the act it was made in, so an act let go of
 *  takes its own change out of a file another act changed too. */
export interface QueuedWrite {
  text: string;
  steps: readonly Step[] | null;
  /** Every act any of it was made in, a whole file's included. */
  acts: readonly string[];
}

/** One change a path waits on, with what it reads again first and the act it was made in. */
interface Step {
  change: FileChange;
  refresh?: Refresh;
  skipIfGone?: boolean;
  findMoved?: FileWrite['findMoved'];
  act?: string;
}

type Refresh = NonNullable<FileWrite['refresh']>;

const pending = new Map<string, QueuedWrite>();
const listeners = new Set<() => void>();
let state: WriteState = 'idle';
let error: string | null = null;
let dropped: DroppedBatch | null = null;
const NONE_REFUSED: readonly string[] = [];
/** The files a newer app wrote that were dropped from their batches: kept past the rest of the
 *  batch landing, until the next edit, so the line saying an edit was lost is read. */
let refused: readonly string[] = NONE_REFUSED;
/** [refused] with the level each file states, which decides what the message offers. */
let refusedFiles: readonly RefusedFile[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let flusher: Flusher | null = null;
let inFlight: Promise<void> | null = null;
/** What the write in flight is carrying, so a withdrawal cannot take back a
 *  file already on its way. */
let sending: ReadonlyMap<string, QueuedWrite> = new Map();
let snapshot: QueueView = { state: 'idle', pending: new Map(), error: null, dropped: null, refused: NONE_REFUSED };

/** The queue is a module singleton because the buffer must outlive any one
 *  screen: navigating away mid-debounce must not drop the edit. */
export function setFlusher(next: Flusher | null): void {
  flusher = next;
}

export function queueWrites(writes: FileWrite[]): void {
  for (const w of writes) {
    const before = pending.get(w.path);
    const onItsWay = before !== undefined && before === sending.get(w.path);
    pending.set(w.path, joined(onItsWay ? undefined : before, queuedOf(w)));
  }
  state = pending.size > 0 ? 'unsaved' : state;
  error = null;
  dropped = null;
  refused = NONE_REFUSED;
  refusedFiles = [];
  publish();

  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void flushWrites(), QUIET_MS);
}

/**
 * For a discrete act — one press, one file, no run to coalesce.
 *
 * The debounce is for gestures that arrive in bursts: a column being
 * renumbered, a paragraph being typed. A status pick is neither, and leaving
 * it to sit for five seconds is what put a reload between the choice and the
 * write. Bursts still degrade gracefully, because a flush that finds one in
 * flight batches behind it rather than racing it.
 */
export function commitWrites(writes: FileWrite[]): Promise<void> {
  queueWrites(writes);
  return flushWrites();
}

function queuedOf(w: FileWrite): QueuedWrite {
  const step: Step | null = w.change
    ? {
        change: w.change,
        ...(w.refresh ? { refresh: w.refresh } : {}),
        ...(w.skipIfGone ? { skipIfGone: true } : {}),
        ...(w.findMoved ? { findMoved: w.findMoved } : {}),
        ...(w.act ? { act: w.act } : {}),
      }
    : null;
  return { text: w.text, steps: step && [step], acts: w.act ? [w.act] : [] };
}

/**
 * What a path waits on once [next] joins what it held, [before]: [next]'s text, and the changes of
 * both in the order they were made — none when either sets the whole file, since the page's text
 * already holds both. It is part of every act either was. A change queued behind one on its way
 * joins it this way once that one has not landed.
 */
function joined(before: QueuedWrite | undefined, next: QueuedWrite): QueuedWrite {
  if (before === undefined) return next;
  return {
    text: next.text,
    steps: before.steps && next.steps ? [...before.steps, ...next.steps] : null,
    acts: [...new Set([...before.acts, ...next.acts])],
  };
}

/** [w] without what [acts] made: itself where none of them made any of it, null where nothing else
 *  is left or where it is a whole file one of them made. */
function withoutActs(w: QueuedWrite, acts: ReadonlySet<string>): QueuedWrite | null {
  if (!w.acts.some((a) => acts.has(a))) return w;
  const steps = w.steps?.filter((step) => !step.act || !acts.has(step.act)) ?? [];
  if (steps.length === 0) return null;
  return { text: w.text, steps, acts: w.acts.filter((a) => !acts.has(a)) };
}

/** [w] as it goes out to [path]: its changes applied in order, each reading again first what it
 *  reads, looking for its file where any of them looks, and never making it where any of them never
 *  does ([skipped] is told when it does not). */
function outgoing(path: string, w: QueuedWrite, skipped: () => void): FileWrite {
  if (!w.steps) return { path, text: w.text };
  const steps = w.steps;
  const refreshes = steps.flatMap((step) => (step.refresh ? [step.refresh] : []));
  const findMoved = steps.find((step) => step.findMoved)?.findMoved;
  return {
    path,
    text: w.text,
    change: steps.length === 1 ? steps[0]!.change : (current) => steps.reduce((text, step) => step.change(text), current),
    ...(refreshes.length > 0
      ? {
          refresh: async (vault) => {
            for (const refresh of refreshes) await refresh(vault);
          },
        }
      : {}),
    ...(findMoved ? { findMoved } : {}),
    ...(steps.some((step) => step.skipIfGone) ? { skipIfGone: true, onSkipped: skipped } : {}),
  };
}

/** Adds the files not yet listed, as a new list. */
function refuse(files: readonly RefusedFile[]): void {
  const fresh = files.filter((f) => !refusedFiles.some((r) => r.path === f.path));
  if (fresh.length === 0) return;
  refusedFiles = [...refusedFiles, ...fresh];
  refused = refusedFiles.map((f) => f.path);
}

export async function flushWrites(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (inFlight) return inFlight;
  if (pending.size === 0 || !flusher) return;

  const queued = new Map(pending);
  /** The files this batch found gone and wrote nothing for; their changes are not sent again. */
  const gone: string[] = [];
  const batch: FileWrite[] = [...queued].map(([path, w]) => outgoing(path, w, () => gone.push(path)));
  sending = queued;
  state = 'saving';
  publish();

  inFlight = (async () => {
    try {
      await flusher!(batch);
      // Only clear what was actually sent — an edit made while the write was
      // in flight is still pending, not saved.
      for (const [path, w] of queued) {
        if (pending.get(path) === w) pending.delete(path);
      }
      state = pending.size > 0 ? 'unsaved' : refused.length > 0 ? 'error' : 'idle';
      error = refused.length > 0 ? refusalMessage(refusedFiles) : null;
      dropped = null;
      if (gone.length > 0) notSaved(goneMessage(gone));
      if (pending.size > 0) timer = setTimeout(() => void flushWrites(), QUIET_MS);
    } catch (e) {
      // Keep the edits. A failed write must not silently discard the work,
      // and the next gesture or the next flush will try again.
      //
      // Except a batch the vault refused as it stands: sent again it would be
      // refused again, and every edit coalesced with it after would go down
      // with it. That batch is dropped here, and its error is where it is said.
      state = 'error';
      if (e instanceof VaultFileFromNewerAppError && queued.has(e.path)) {
        // Only that file, and what each act it was part of changed with it, waits for an update; the
        // rest of the batch goes out again as it stands.
        const acts = new Set(queued.get(e.path)!.acts);
        for (const [path, w] of queued) {
          const kept = path === e.path ? null : withoutActs(w, acts);
          const behind = pending.get(path);
          if (behind === w) {
            if (kept) pending.set(path, kept);
            else pending.delete(path);
          } else if (behind && kept) {
            pending.set(path, joined(kept, behind));
          }
        }
        dropped = null;
        refuse([{ path: e.path, level: e.level }]);
        error = refusalMessage(refusedFiles);
        if (pending.size > 0) timer = setTimeout(() => void flushWrites(), QUIET_MS);
      } else if (e instanceof VaultWriteRefusedError || e instanceof VaultFileFromNewerAppError) {
        // Refused as it stands, and a refusal naming no file of the batch cannot be taken out of
        // it: sent again it would be refused again.
        const lost: string[] = [];
        for (const [path, w] of queued) {
          if (pending.get(path) !== w) continue;
          pending.delete(path);
          lost.push(path);
        }
        if (e instanceof VaultFileFromNewerAppError) {
          dropped = { fileCount: batch.length, atMs: Date.now(), cause: 'newer-app' };
          refuse(lost.map((path) => ({ path, level: e.level })));
          error = refusedFiles.length > 0 ? refusalMessage(refusedFiles) : e.message;
        } else {
          dropped = { fileCount: batch.length, atMs: Date.now(), cause: 'too-big' };
          error = `Too many changes at once (${batch.length} files) — nothing was saved. Reload, then make the change in smaller steps.`;
        }
      } else {
        // A change queued behind this batch was started from it, as one on its way; with the batch
        // not landed, it goes out again under that change.
        for (const [path, w] of queued) {
          const behind = pending.get(path);
          if (behind && behind !== w) pending.set(path, joined(w, behind));
        }
        dropped = null;
        error = e instanceof Error ? e.message : 'The change was not saved.';
      }
    } finally {
      inFlight = null;
      sending = new Map();
      publish();
    }
  })();

  return inFlight;
}

/** What was not saved and will not be sent again, said where a failed write is until the next edit
 *  or write. */
function notSaved(message: string): void {
  state = 'error';
  error = message;
  dropped = null;
}

/**
 * An act none of whose writes were queued, since a file it is made on could not be read: said
 * ([notSaved]) once any write on its way has landed. Nothing of it is kept to send again.
 */
export function failedBeforeQueued(message: string): void {
  const say = () => {
    notSaved(message);
    publish();
  };
  if (inFlight) void inFlight.then(say);
  else say();
}

/** A write queued for an act that can still be undone, with what its path
 *  held in the queue before it (undefined for nothing). */
export interface HeldWrite extends FileWrite {
  before: QueuedWrite | undefined;
}

/**
 * Takes back writes that have not gone out yet — an Undo inside the
 * debounce. Each path returns to what it held before, so an earlier edit
 * the write was coalesced over survives. All or nothing: false, and nothing
 * changed, when any of them has been sent, or written over since.
 */
export function withdrawWrites(held: readonly HeldWrite[]): boolean {
  const untouched = held.every((w) => pending.get(w.path)?.text === w.text && sending.get(w.path)?.text !== w.text);
  if (!untouched) return false;
  for (const w of held) {
    if (w.before === undefined) pending.delete(w.path);
    else pending.set(w.path, w.before);
  }
  if (pending.size === 0) {
    if (timer) clearTimeout(timer);
    timer = null;
    if (state === 'unsaved') state = 'idle';
  }
  publish();
  return true;
}

export function subscribeQueue(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function queueSnapshot(): QueueView {
  return snapshot;
}

/** Whatever this path should read as right now — the pending text if there is
 *  one, so a surface never shows a version older than the edit just made. */
export function pendingText(path: string): string | undefined {
  return pending.get(path)?.text;
}

/** What [path] is waiting to have written, for a write that may be taken back to restore. */
export function pendingWrite(path: string): QueuedWrite | undefined {
  return pending.get(path);
}

function publish(): void {
  snapshot = { state, pending: new Map([...pending].map(([path, w]) => [path, w.text])), error, dropped, refused };
  for (const listener of listeners) listener();
}

/** Nothing is lost on the way out. `visibilitychange` covers the cases
 *  `beforeunload` misses on mobile, and neither can be relied on alone. */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushWrites();
  });
  window.addEventListener('beforeunload', (event) => {
    void flushWrites();
    // The flush above cannot be waited for — a page being torn down does not
    // hold for a network round trip — so a leave inside the debounce takes the
    // edit with it. And a FAILED write is worse: the queue keeps it and every
    // surface goes on showing it, so the board still reads `done` while the
    // vault still holds `active`, and a reload discards the difference without
    // a word. The buffer lives in this tab's memory and nowhere else.
    //
    // Hence the one place this app is allowed to block on the way out. The
    // browser's own dialog is blunt, and it is still better than a status
    // change that silently did not happen.
    if (pending.size === 0) return;
    event.preventDefault();
    // Older engines want the assignment rather than the call.
    event.returnValue = '';
  });
}
